import React, { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../supabase/client";

const AuthContext = createContext({ loading: true, user: null, profile: null, subscriptionAccess: null });
const cacheKeys = ["email", "userEmail", "companyId", "companyName", "role", "userName"];

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({ loading: true, user: null, profile: null, subscriptionAccess: null });
  useEffect(() => {
    let active = true;
    let generation = 0;
    const restore = async (session, request = ++generation) => {
      const current = () => active && request === generation;
      if (!current()) return;
      setAuth((previous) => previous.user?.id && previous.user.id === session?.user?.id
        ? previous : { loading: true, user: null, profile: null, subscriptionAccess: null });
      try {
        if (!session?.access_token) throw new Error("No session");
        const { data, error } = await supabase.auth.getUser(session.access_token);
        if (error || !data?.user?.email) throw new Error("Invalid session");
        const { data: profile, error: profileError } = await supabase.from("profiles")
          .select("id, company, company_name, role, driver_name")
          .eq("email", data.user.email).single();
        if (profileError || !profile?.company?.trim() || !profile.role) throw new Error("Missing profile");
        if (!current()) return;
        const values = [profile.company.trim(), data.user.email, data.user.id,
          profile.company_name || "", profile.role, profile.driver_name || ""];
        cacheKeys.forEach((key, i) => localStorage.setItem(key, values[i]));
        const { data: subscriptionAccess, error: accessError } = await supabase.functions.invoke(
          "account-subscription", { body: { action: "access_status" } }
        );
        const verifiedAccess = !accessError && typeof subscriptionAccess?.allowed === "boolean"
          ? subscriptionAccess
          : { managed: true, allowed: false, requirePayment: true, verificationFailed: true };
        if (!current()) return;
        setAuth({ loading: false, user: data.user, profile, subscriptionAccess: verifiedAccess });
      } catch {
        if (!current()) return;
        cacheKeys.forEach((key) => localStorage.removeItem(key));
        setAuth({ loading: false, user: null, profile: null, subscriptionAccess: null });
      }
    };
    // Schedule outside the Auth callback so profile queries do not hold its lock.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const request = ++generation;
      setTimeout(() => { void restore(session, request); }, 0);
    });
    const initialGeneration = generation;
    supabase.auth.getSession().then(({ data }) => {
      if (active && generation === initialGeneration) void restore(data?.session);
    }).catch(() => {
      if (active && generation === initialGeneration) void restore(null);
    });
    const refreshSubscriptionAccess = () => {
      const request = ++generation;
      supabase.auth.getSession().then(({ data }) => {
        if (active) void restore(data?.session, request);
      });
    };
    window.addEventListener("subscription-access-changed", refreshSubscriptionAccess);
    return () => {
      active = false;
      generation += 1;
      subscription.unsubscribe();
      window.removeEventListener("subscription-access-changed", refreshSubscriptionAccess);
    };
  }, []);
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
